import { Component, EventEmitter, Injector, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  TaskWatcherServiceProxy,
  UserServiceProxy,
  TaskServiceProxy,
  UserNotificationSubscriptionServiceProxy,
  NotifyUserInput,
  ResponseNotificationDto,
  UserNotificationType,
} from '@shared/service-proxies/service-proxies';
import { AbpSessionService } from 'abp-ng2-module';
import { AppComponentBase } from '@shared/common/app-component-base';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UserSignalrService } from '@app/shared/layout/change-status/user-signalr.service';

@Component({
  selector: 'vf-task-watcher',
  templateUrl: './task-watcher.component.html',
})
export class TaskWatcherComponent extends AppComponentBase implements OnInit {
  @Input() isForNewTask: boolean = false;
  @Input() taskId: number;
  @Input() watchers: any[] = [];
  @Input() assignedUserId?: number;
  @Input() contributors: any[] = [];
  @Input() teamMembers?: any[] = [];
  @Input() taskItem: any;

  @Output() watchersChanged = new EventEmitter<any[]>();
  @Output() watchersSelected = new EventEmitter<number[]>();
  @Output() watchThisTask = new EventEmitter<boolean>();

  searchText = '';
  private searchTextChanged: Subject<string> = new Subject<string>();

  allUsers: any[] = [];
  isWatching = false;
  listOfUsers: any[] = [];
  isLoading = false;
  currentUser: any;
  signalrEvents: Subscription;
  
  constructor(
    injector: Injector,
    private _taskWatcherService: TaskWatcherServiceProxy,
    private _userService: UserServiceProxy,
    private _taskService: TaskServiceProxy,
    private _userSignalrService: UserSignalrService,
    private _notificationService: UserNotificationSubscriptionServiceProxy,
  ) { super(injector); }

  ngOnInit(): void {
    this.getWatchStatus();
    this.getWatchers();
    this.getTenantUsers();

    this.searchTextChanged.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => this.initializeListOfUsers());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assignedUserId'] || changes['watchers'] || changes['contributors']) {
      this.initializeListOfUsers();
    }
  }

  private getWatchStatus(): void {
    if (!this.taskId) return;
    this._taskWatcherService.checkTaskStatus(this.appSession.userId, this.taskId)
      .subscribe((res: any) => {
        this.isWatching = res.responseData ?? false;
      });
  }

  private getTenantUsers(): void {
    this._userService.getTenantUsers().subscribe((result: any) => {
      let users = result.responseData || [];

      if (this.isForNewTask && this.taskItem?.isPrivate) {
        const teamIds = this.teamMembers.map(m => m.userId);
        users = users.filter(u => teamIds.includes(u.userId));
        this.watchers = this.watchers.filter(w => teamIds.includes(w.userId));
      } else if (!this.isForNewTask && this.taskItem?.isPrivate) {
        const teamIds = this.teamMembers.map(m => m.id);
        users = users.filter(u => teamIds.includes(u.userId));
      }

      this.allUsers = users;
      this.currentUser = this.allUsers.find(u => u.userId === this.appSession.userId);
      this.initializeListOfUsers();
    });
  }

  toggleWatching(): void {
    if (!this.isForNewTask) {
      if (this.isWatching) {
        this._taskWatcherService.unwatchTask(this.appSession.userId, this.taskId).subscribe(() => {
          this.isWatching = false;
          this.getWatchers();
        });
      } else {
        this._taskWatcherService.watchTask(this.appSession.userId, this.taskId).subscribe(() => {
          this.isWatching = true;
          this.getWatchers();
        });
      }
    } else {
      const watcherIndex = this.watchers.findIndex(w => w.userId === this.currentUser.userId);
      
      if (watcherIndex > -1) {
        this.watchers.splice(watcherIndex, 1);
        this.isWatching = false;
      } else {
        this.watchers.push({
          userId: this.currentUser.userId,
          userName: this.currentUser.fullname,
          profilePictureUrl: this.currentUser.profilePictureUrl,
          userInitials: this.getInitials(this.currentUser.fullname)
        });
        this.isWatching = true;
      }

      this.watchersSelected.emit(this.watchers);
      this.watchThisTask.emit(this.isWatching);
      this.initializeListOfUsers(); // 🟢 NEW: оновлення списку після toggle
    }
  }

  // Confirmation modal state for contributor → watcher role swap
  showDemoteConfirm: boolean = false;
  pendingDemoteUser: any = null;

  addWatcher(user: any): void {
    let isCurrentContributor: boolean = false;

    if (this.watchers.find(w => w.userId === user.id)) {
      return;
    }
    // Role exclusivity: contributor → watcher requires confirmation
    isCurrentContributor = (this.taskItem?.contributors || []).some((c: any) =>
      (c.assignedToId || c.userId || c.id) === user.id
    );
    if (isCurrentContributor) {
      this.pendingDemoteUser = user;
      this.showDemoteConfirm = true;
      return;
    }
    this.applyAddWatcher(user);
  }

  confirmDemoteToWatcher(): void {
    const user = this.pendingDemoteUser;
    this.showDemoteConfirm = false;
    this.pendingDemoteUser = null;
    if (!user) {
      return;
    }
    this.demoteContributorToWatcher(user);
    this.applyAddWatcher(user);
  }

  cancelDemoteToWatcher(): void {
    this.showDemoteConfirm = false;
    this.pendingDemoteUser = null;
  }

  private applyAddWatcher(user: any): void {
    if (this.isForNewTask) {
      this.watchers.push({
        userId: user.id,
        userName: user.name,
        profilePictureUrl: user.profilePictureUrl,
        userInitials: this.getInitials(user.name)
      });
      this.watchersSelected.emit(this.watchers);
    } else {
      this._taskWatcherService.watchTask(user.id, this.taskId).subscribe(
        (res: any) => {
          if (res?.isError) {
            let options = this.toastrOptions;
            this.toastr.error(res.errorMessage || 'Failed to add watcher. Please try again.', null, options);
            return;
          }
          this.getWatchers();
          // Only notify when someone else (not the current user) was added as a watcher
          if (user.id !== this.appSession.userId) {
            this._sendWatcherAddedNotification(user.id);
          }
        },
        () => {
          let options = this.toastrOptions;
          this.toastr.error('Failed to add watcher. Please try again.', null, options);
        }
      );
    }
    this.listOfUsers = this.listOfUsers.filter(u => u.id !== user.id);
  }

  private _sendWatcherAddedNotification(watcherUserId: number): void {
    const addedBy = this.appSession.user;
    const rawDue = this.taskItem?.dueDate;
    const dueDate = rawDue
      ? (typeof (rawDue as any).format === 'function'
        ? (rawDue as any).format('YYYY-MM-DDTHH:mm:ss')
        : String(rawDue))
      : undefined;
    const input = new NotifyUserInput({
      notifType: UserNotificationType._5,
      notificationDto: new ResponseNotificationDto({
        userId: watcherUserId,
        taskTitle: this.taskItem?.title,
        dueDate: dueDate,
        projectName: this.taskItem?.taskProjectName || this.taskItem?.projectName,
        fullName: addedBy ? `${addedBy.name} ${addedBy.surname}` : undefined,
        firstName: addedBy?.name,
      }),
    });
    this._notificationService.notifyUser(input).subscribe({
      error: () => { /* notification failure does not affect the watcher add */ }
    });
  }

  private demoteContributorToWatcher(user: any): void {
    let stepsForUser: any[] = [];

    if (!this.taskItem?.taskCheckLists) {
      return;
    }
    stepsForUser = this.taskItem.taskCheckLists.filter((cl: any) => cl.assignedToId === user.id);
    // Unassign each step (preserve work, mark as shared) — backend Contributors derives from step assignment
    stepsForUser.forEach((step: any) => {
      step.assignedToId = undefined;
      this._taskService.createOrUpdateCheckList(step).subscribe();
    });
    if (this.taskItem.contributors) {
      this.taskItem.contributors = this.taskItem.contributors.filter((c: any) =>
        (c.assignedToId || c.userId || c.id) !== user.id
      );
    }
  }

  removeWatcher(userId: number): void {
    if (this.isForNewTask) {
      this.watchers = this.watchers.filter(w => w.userId !== userId);
      this.watchersSelected.emit(this.watchers);
    } else {
      this._taskWatcherService.unwatchTask(userId, this.taskId).subscribe(() => {
        this.getWatchers();
      });
    }

    if(userId === this.appSession.userId) {
      this.isWatching = false;
      this.watchThisTask.emit(this.isWatching);
    }
    // 🟢 NEW: додаємо користувача назад у список
    const userToAdd = this.allUsers.find(u => u.userId === userId);
    if (userToAdd) {
      const fullName = userToAdd.fullname || userToAdd.name;
      this.listOfUsers.push({
        id: userToAdd.userId,
        name: fullName,
        email: userToAdd.email,
        profilePictureUrl: userToAdd.profilePictureUrl ?? null,
        isWatchingThisTask: false,
        userInitials: this.getInitials(fullName)
      });
    }
  }

  private getWatchers(): void {
    if (!this.taskId) return;

    this._taskWatcherService.getTaskWatchers(this.taskId).subscribe({
      next: (res) => {
        this.watchers = res.responseData.map(w => ({
          userId: w.userId,
          userName: w.username,
          profilePictureUrl: w.profilePictureUrl,
          userInitials: this.getInitials(w.username)
        }));
        this.initializeListOfUsers();
      },
      error: () => {
        let options = this.toastrOptions;
          this.toastr.success('Failed to fetch watchers. Please try again.', null, options);
        this.watchers = [];
        this.initializeListOfUsers();
      }
    });
  }

  private initializeListOfUsers(): void {
    let contributorIds: number[] = [];

    if (!this.allUsers?.length) {
      this.listOfUsers = [];
      return;
    }

    const watcherIds: number[] = this.watchers.map(w => w.userId);
    // Prefer the dedicated @Input (Angular change detection sees it).
    // Fall back to taskItem.contributors for templates that don't pass the
    // explicit binding yet.
    const contribSource: any[] = (this.contributors && this.contributors.length)
      ? this.contributors
      : (this.taskItem?.contributors || []);
    contributorIds = contribSource.map(
      (c: any) => c.assignedToId || c.userId || c.id
    );

    // Hide:
    //   - The current assignee (already implicitly part of the task)
    //   - Existing contributors (role exclusivity — they can't also be watchers)
    //   - Users already watching this task (they appear in the chip list below)
    // The current user stays in the list when they aren't the assignee or a
    // contributor, so they can add themselves as a watcher.
    this.listOfUsers = this.allUsers
      .filter(user =>
        user.userId !== this.assignedUserId &&
        !watcherIds.includes(user.userId) &&
        !contributorIds.includes(user.userId)
      )
      .map(user => {
        const fullName = user.fullname || user.name || '';
        return {
          id: user.userId,
          name: fullName,
          email: user.email,
          profilePictureUrl: user.profilePictureUrl ?? null,
          isWatchingThisTask: false,
          userInitials: this.getInitials(fullName)
        };
      });
  }

  onSearchTextChange(): void {
    this.searchTextChanged.next(this.searchText);
  }

  get filteredListOfUsers() {
    return this.listOfUsers.filter(u =>
      u.name?.toLowerCase().includes(this.searchText.toLowerCase())
    );
  }
}
